import React, { Component } from 'react';
import { withApollo } from 'react-apollo';
import { Table as DvExTable } from '@devexpress/dx-react-grid-material-ui';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import _debounce from 'lodash/debounce';
import _isNumber from 'lodash/isNumber';

import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import Table from '../../../components/Global/Table/Table';
import ErrorNotification from '../../../components/common/ErrorNotification';
import TextFieldInput from '../../../components/Global/Inputs/TextField';

import combineStyles from '../../../utils/combineStyles';
import LayoutStyles from '../../../styles/layout';
import ButtonStyles from '../../../styles/button';
import TypographyStyles from '../../../styles/typography';
import SpacingStyles from '../../../styles/helpers/spacing';

import OrganizationsTableMetadata from './OrganizationsTable.metadata';

import * as Queries from '../../../graphQL/queries/orgQueries';
import { AppContext } from '../../../AppProvider';

import {
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_DEBOUNCE_DELAY
} from '../../../constants/constants';

const styles = theme => ({
  titleWrapper: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing.unit * 5
  },
  title: {
    flex: 1
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(12,31,44, 0.04)'
    }
  },
  searchRoot: {
    '& fieldset': {
      border: '0 !important'
    }
  }
});

export class OrganizationsIndex extends Component {
  state = {
    organizations: [],
    loading: true,
    error: null,
    isSubmitting: false,
    searchText: '',
    page: 1,
    rowsPerPage: 25
  };

  async componentDidMount() {
    await this._getOrganizationList();
  }

  _getOrganizationList = async () => {
    this.setState({ loading: true });
    const { client: ApolloClient } = this.props;
    const { page, rowsPerPage, searchText } = this.state;
    const offset = page * rowsPerPage;

    try {
      const {
        data: {
          organization: {
            paged: { results: organizationRows, totalCount }
          }
        }
      } = await ApolloClient.query({
        query: Queries.paginatedOrganizations,
        variables: {
          limit: rowsPerPage,
          offset,
          searchText
        },
        fetchPolicy: 'network-only'
      });
      const organizations = OrganizationsTableMetadata.formatData(
        organizationRows
      );
      this.setState({
        organizations,
        totalCount: _isNumber(totalCount) ? totalCount : this.state.totalCount,
        loading: false
      });
    } catch (error) {
      this.setState({ error });
    }
  };

  _changePage = page => {
    this.setState({ page, loading: true }, this._getOrganizationList);
  };

  _changeRowsPerPage = rowsPerPage => {
    localStorage.setItem('PropelRowsPerPage', rowsPerPage);
    this.setState({ rowsPerPage: rowsPerPage, page: 0 });
    const rows = rowsPerPage === 0 ? DEFAULT_ROWS_PER_PAGE : rowsPerPage;
    this.setState(
      { rowsPerPage: rows, page: 0, loading: true },
      this._getOrganizationList
    );
  };

  _handleUpdateInput = ({ target: { value } }) => {
    this._searchCallback(value);
  };

  _searchCallback = _debounce(
    searchText => {
      this.setState({ searchText, page: 0 }, this._getOrganizationList);
    },
    DEFAULT_DEBOUNCE_DELAY,
    {
      leading: false,
      trailing: true
    }
  );

  _handleRowClick = row =>
    this.props.history.push(`/organization/edit/${row.id}`);

  render() {
    const { t, classes } = this.props;

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    return (
      <div>
        <div className={classes.titleWrapper}>
          <h1
            className={classnames(
              classes.headline4,
              classes.noMarginBottom,
              classes.title
            )}
          >
            {t('Organizations')}
          </h1>
          <Button
            className={classes.button}
            classes={{
              label: classes.buttonLabel
            }}
            variant="contained"
            color="primary"
            component={Link}
            to={'/organization/new'}
          >
            {t('Add Organization')}
          </Button>
        </div>
        <div
          className={classnames(
            classes.wrapper,
            classes.noPadding,
            classes.marginBottom2
          )}
        >
          <TextFieldInput
            name={'searchText'}
            value={this.state.searchText}
            onChange={this._handleUpdateInput}
            classes={{
              root: classnames(classes.noMarginBottom, classes.searchRoot)
            }}
            startAdornment={<SearchIcon className={classes.searchIcon} />}
            placeholder={t('Search…')}
            textFieldType="search"
          />
        </div>
        <div>
          <Table
            name={t('Organization')}
            columns={OrganizationsTableMetadata.columns({ t })}
            data={this.state.organizations}
            orderBy="id"
            order={'asc'}
            page={0}
            rowsPerPage={DEFAULT_ROWS_PER_PAGE}
            rowComponent={props => (
              <DvExTable.Row
                {...props}
                className={classes.tableRow}
                onClick={() => this._handleRowClick(props.row)}
              />
            )}
            totalCount={this.state.totalCount}
            loading={this.state.loading}
            withDownload={false}
            withColumnChooser={false}
            withSelection={false}
            withServerPaging={false}
          />
        </div>
      </div>
    );
  }
}

OrganizationsIndex.contextType = AppContext;
const OrganizationsIndexWithTranslation = withNamespaces()(OrganizationsIndex);
export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    styles
  )
)(withApollo(OrganizationsIndexWithTranslation));
